/* mapReportData — backend ReportData JSON (report{...}, controlImplementations[],
   assessmentHistory[], ...) -> FE ReportPayload. No sample/mock fallback; missing
   fields render as empty/zero. There is no top-level controlStatuses[] -
   controlTitle/statementText already ride along on report.controlEvidences[],
   merged server-side. CAR rows come from controlStatusList (always populated),
   enriched by controlId from controlEvidences (may be [] mid-review) — see
   mapControlEvidences below. */
import type {
  AssessmentResultRow,
  CatalogSection,
  CiaLevel,
  ControlImplSection,
  ExecutiveSection,
  FindingRow,
  FindingsSection,
  FindingsSnapshot,
  HistorySection,
  ImplControl,
  ReportPayload,
  TrendSection,
} from "../types/reportTypes";
import { useAuthStore } from "../../../../stores/authStore";

export type ReportDataDto = Record<string, any>;

function safeString(v: unknown): string {
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

const ciaLevel = (v: unknown): CiaLevel => {
  const s = safeString(v).toLowerCase();
  if (s === "high" || s === "moderate" || s === "low") return s;
  if (s === "medium") return "moderate";
  return "unknown";
};

const SEVERITIES = new Set(["critical", "high", "medium", "low"]);
const severityOf = (v: unknown): FindingRow["severity"] => {
  const s = safeString(v).toLowerCase();
  return (SEVERITIES.has(s) ? s : "none") as FindingRow["severity"];
};

/* backend finding status → findings-snapshot status bucket */
const STATUS_BUCKET: Record<string, keyof FindingsSnapshot["byStatus"]> = {
  identified_finding: "identified",
  remediation_in_progress: "inProgress",
  remediation_on_hold: "onHold",
  validation_and_testing: "validation",
  completed: "completed",
  closed_remediated: "closed",
  accepted_risk: "riskAccepted",
};

const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
/** ISO / date string → "DD/MM/YYYY" (empty string if unparseable). Timezone-agnostic for ISO dates. */
function fmtDate(iso: unknown): string {
  if (!iso) return "";
  const s = safeString(iso).trim();
  if (!s || s === "—") return "";
  const ymdMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/.exec(s);
  if (ymdMatch) {
    return `${pad(Number(ymdMatch[3]))}/${pad(Number(ymdMatch[2]))}/${ymdMatch[1]}`;
  }
  const dmyMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (dmyMatch) {
    return `${pad(Number(dmyMatch[1]))}/${pad(Number(dmyMatch[2]))}/${dmyMatch[3]}`;
  }
  if (/^\d{10,13}$/.test(s)) {
    const num = Number(s);
    const d = new Date(s.length === 10 ? num * 1000 : num);
    if (!Number.isNaN(d.getTime())) {
      return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
    }
  }
  const d = new Date(s + (/\d{2}:\d{2}/.test(s) ? "" : "T00:00:00"));
  if (Number.isNaN(d.getTime())) return "";
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

function splitLevel(val: string): string[] {
  if (!val) return [];
  const delim = val.includes("—") ? "—" : "-";
  return val.split(delim).map((s) => s.trim());
}

function cleanVersion(ver: string): string {
  const m = /v?([\d.]+)/i.exec(ver);
  if (m && (ver.toLowerCase().includes("profile") || ver.startsWith("(") || /^v/i.test(ver))) {
    return m[1];
  }
  return ver.replace(/^v/i, "").replace(/[()]/g, "").trim();
}

function extractProfileStatus(desc: string): string {
  const descMatch = /Baseline profile\s*[-—]\s*([-\w]+)/i.exec(desc);
  if (descMatch) return descMatch[1].trim().toLowerCase();
  const knownStatus = /\b(published|draft|approved|archived|active|in_review)\b/i.exec(desc);
  return knownStatus ? knownStatus[1].trim().toLowerCase() : "";
}

function parseCatalogDetails(cat: any): { title: string; version: string; status: string } {
  const catLvl = splitLevel(cat?.profileLevel ?? "");
  const rawVersion = cat?.version ?? cat?.versionNumber ?? cat?.oscalVersion ?? catLvl[0] ?? "";
  const catVersion = cleanVersion(String(rawVersion));
  const rawStatus = cat?.status ?? cat?.lifecycleStatus ?? catLvl[1] ?? "";
  const catStatus = String(rawStatus).trim().toLowerCase();
  return {
    title: cat?.catalogName ?? cat?.title ?? "",
    version: catVersion || "1.0.0",
    status: catStatus,
  };
}

function parseProfileDetails(prof: any, raw?: any): { title: string; version: string; status: string } {
  const profLvl = splitLevel(prof?.profileLevel ?? "");
  let profVersion = String(
    prof?.version ??
    prof?.versionNumber ??
    raw?.profile?.version ??
    raw?.report?.profile?.version ??
    ""
  ).trim();
  let profStatus = String(
    prof?.status ??
    prof?.lifecycleStatus ??
    prof?.profileStatus ??
    raw?.profile?.status ??
    raw?.report?.profile?.status ??
    ""
  ).trim();

  if (profLvl.length >= 2) {
    if (!profVersion) profVersion = profLvl[0];
    if (!profStatus) profStatus = profLvl[1];
  } else if (!profVersion && prof?.profileLevel) {
    profVersion = String(prof.profileLevel).trim();
  }

  profVersion = cleanVersion(profVersion);

  if (!profStatus && prof?.description) {
    profStatus = extractProfileStatus(String(prof.description));
  } else {
    profStatus = profStatus.toLowerCase();
  }

  return {
    title: prof?.catalogName ?? prof?.title ?? raw?.profile?.title ?? raw?.report?.profile?.title ?? "",
    version: profVersion || "1.0.0",
    status: profStatus,
  };
}

/* report.catalogs: [{type:"CATALOG"|"PROFILE", catalogName, profileLevel, description, …}] */
function mapCatalog(cats: any[] | undefined, raw?: any): CatalogSection {
  const catList = Array.isArray(cats) ? cats : [];
  const cat =
    catList.find(
      (c) =>
        c?.type === "CATALOG" ||
        (!c?.type && !String(c?.profileLevel ?? "").toLowerCase().includes("profile"))
    ) ?? catList[0];
  const prof =
    catList.find(
      (c) =>
        c?.type === "PROFILE" ||
        (!c?.type && String(c?.profileLevel ?? "").toLowerCase().includes("profile"))
    ) ?? catList[1];

  return {
    catalog: parseCatalogDetails(cat),
    profile: parseProfileDetails(prof, raw),
  };
}

const toDetermination = (v: unknown): AssessmentResultRow["determination"] => {
  const s = safeString(v).toLowerCase();
  if (s === "satisfied") return "satisfied";
  if (s === "pending") return "pending";
  return "not-satisfied";
};

/* evidenceType is always "Manual"; executionMethod is the field that actually varies per row. */
const EXECUTION_METHOD_LABELS: Record<string, string> = {
  opa: "Automation",
  declarative: "Declarative",
  declarative_evidence: "Declarative + Evidence",
};
/* Fallback path (no executionMethod) reads the raw evidenceType field, which can carry
   "Automated" instead of the "Automation" label used by EXECUTION_METHOD_LABELS and the
   CAR Type filter's <option value>. Normalize it so the filter keeps matching that row. */
const EVIDENCE_TYPE_SYNONYMS: Record<string, string> = {
  automated: "Automation",
};
const typeLabel = (e: any): string | undefined => {
  const method = EXECUTION_METHOD_LABELS[String(e?.executionMethod ?? "").toLowerCase()];
  if (method) return method;
  const raw = e?.evidenceType;
  return EVIDENCE_TYPE_SYNONYMS[String(raw ?? "").toLowerCase()] ?? raw;
};

export function cleanEvidenceHtml(html?: string): string {
  if (!html || html === "No evidence") return "";
  const cleaned = String(html)
    .replace(/<svg\b[^>]*>[\s\S]*?<\/svg>/gi, "")
    .replace(/<img\b[^>]*\/?>/gi, "")
    .replace(/<i\b[^>]*>[\s\S]*?<\/i>/gi, "")
    .replace(/<br\s*\/?>/gi, "")
    .trim();
  return cleaned.replace(/(<a\b[^>]*>)\s*(<\/a>)/gi, "$1Preview$2");
}

const mapEvidenceRow = (e: any): AssessmentResultRow => ({
  controlId: e?.controlId ?? "",
  controlTitle: e?.controlTitle ?? "",
  statementId: e?.statementId ?? "",
  statement: e?.statementText ?? "",
  determination: toDetermination(e?.auditStatus),
  evidence: [],
  evidenceType: typeLabel(e),
  dataSourceHtml: cleanEvidenceHtml(e?.dataSource),
  justification: e?.justification,
  failedAssetCount: e?.failedAssetCount,
  passedAssetCount: e?.passedAssetCount,
  failedAssetsJson: e?.failedAssetsJson,
  passedAssetsJson: e?.passedAssetsJson,
});

/* CAR rows: one per controlStatusList entry, enriched by controlId from controlEvidences.
   Also includes any executed controls in controlEvidences that may not be in controlStatusList,
   ensuring the report displays all controls assessed in the assessment execution. */
function mapControlEvidences(car: {
  controlEvidences?: any[];
  controlStatusList?: any[];
}): AssessmentResultRow[] {
  const statusList = Array.isArray(car?.controlStatusList) ? car.controlStatusList : [];
  const evidenceList = Array.isArray(car?.controlEvidences) ? car.controlEvidences : [];

  // No status list (older payload shape) — fall back to evidence-only rows.
  if (!statusList.length) {
    return [...evidenceList]
      .sort((a, b) => (a?.rowOrder ?? 0) - (b?.rowOrder ?? 0))
      .map((e) => mapEvidenceRow(e));
  }

  const normId = (id: unknown) => safeString(id).trim().toLowerCase();
  const matchedEvidenceIds = new Set<string>();

  // Diagnostic: controlStatusList ids with no matching / blank-title evidence entry.
  const noMatch: string[] = [];
  const blankTitle: string[] = [];
  for (const row of statusList) {
    const id = String(row?.controlId ?? "");
    const nid = normId(id);
    const evidence = evidenceList.find((e: any) => normId(e?.controlId) === nid);
    if (!evidence) noMatch.push(id);
    else if (!evidence.controlTitle) blankTitle.push(id);
  }
  console.log("[audit-report] CAR match summary —", {
    controlStatusListCount: statusList.length,
    controlEvidencesCount: evidenceList.length,
    noMatchingEvidence: noMatch,
    matchedButBlankTitle: blankTitle,
  });

  const rows: { rowOrder?: number; row: AssessmentResultRow }[] = statusList.map((row) => {
    const id = String(row?.controlId ?? "");
    const nid = normId(id);
    const evidence = evidenceList.find((e: any) => normId(e?.controlId) === nid);
    if (evidence) matchedEvidenceIds.add(nid);

    return {
      rowOrder: row?.rowOrder ?? evidence?.rowOrder,
      row: {
        controlId: id,
        controlTitle: evidence?.controlTitle ?? "",
        statementId: evidence?.statementId ?? "",
        statement: evidence?.statementText ?? "",
        determination: toDetermination(row?.auditStatus ?? evidence?.auditStatus),
        evidence: [],
        evidenceType: typeLabel(evidence),
        dataSourceHtml: cleanEvidenceHtml(evidence?.dataSource),
        justification: evidence?.justification,
        failedAssetCount: evidence?.failedAssetCount,
        passedAssetCount: evidence?.passedAssetCount,
        failedAssetsJson: evidence?.failedAssetsJson,
        passedAssetsJson: evidence?.passedAssetsJson,
      },
    };
  });

  // Include any extra controls that were executed in evidenceList but missing from statusList
  // (e.g. controls executed in an assessment plan that aren't in the baseline profile).
  const extraEvidences = evidenceList.filter(
    (e: any) => !matchedEvidenceIds.has(normId(e?.controlId))
  );
  for (const e of extraEvidences) {
    rows.push({
      rowOrder: e?.rowOrder,
      row: mapEvidenceRow(e),
    });
  }

  // Preserve rowOrder if present across rows
  if (rows.some((r) => typeof r.rowOrder === "number")) {
    rows.sort((a, b) => (a.rowOrder ?? 9999) - (b.rowOrder ?? 9999));
  }

  return rows.map((r) => r.row);
}

/* controlImplementations: supports both the Auditor Report GroupControlImplementation[]
   (each containing sspBaselineTotal, summary, controls[]) and the legacy flat row list. */
function mapControlImplementation(list: any[] | undefined): ControlImplSection {
  if (!Array.isArray(list)) return { profileTitle: "", groups: [] };
  const groups: ControlImplSection["groups"] = [];
  const byKey: Record<string, ControlImplSection["groups"][number]> = {};

  for (const it of list) {
    // Group object shape (Auditor Report API contract)
    if (it && Array.isArray(it.controls)) {
      const key = String(it.groupId ?? it.key ?? "");
      const title = String(it.groupTitle ?? it.title ?? "");
      const baselineTotal = Number(it.sspBaselineTotal ?? it.controls.length ?? 0);
      const s = it.summary;
      const groupSummary = s ? {
        assessedControlCount: Number(s.assessedControlCount ?? it.controls.length ?? 0),
        notAssessedControlCount: Number(
          s.notAssessedControlCount ?? Math.max(0, baselineTotal - (s.assessedControlCount ?? it.controls.length ?? 0))
        ),
        implementedControlCount: Number(s.implementedControlCount ?? 0),
        notImplementedControlCount: Number(s.notImplementedControlCount ?? 0),
        passedControlCount: Number(s.passedControlCount ?? 0),
        failedControlCount: Number(s.failedControlCount ?? 0),
        pendingControlCount: Number(s.pendingControlCount ?? 0),
      } : undefined;

      const controls: ImplControl[] = it.controls.map((c: any) => ({
        controlId: String(c?.controlId ?? ""),
        title: String(c?.controlTitle ?? c?.title ?? ""),
        status: (c?.implementationStatus ?? c?.status ?? "not-implemented") as ImplControl["status"],
        implementationStatus: String(c?.implementationStatus ?? c?.status ?? "not-implemented"),
        auditStatus: c?.auditStatus ? (String(c.auditStatus) as ImplControl["auditStatus"]) : undefined,
        isEnhancement: !!c?.isEnhancement,
        components: (c?.components ?? []).map((x: any) => ({
          name: x?.name ?? "",
          type: x?.type ?? "",
          status: x?.status ?? "",
        })),
        inventory: (c?.inventoryItems ?? c?.inventory ?? []).map((iv: any) => ({
          device: String(iv?.description ?? iv?.device ?? "").replace(/^Device:\s*/i, ""),
          component: iv?.componentName ?? iv?.component,
          assets: (iv?.assets ?? []).map((a: any) => ({ type: a?.type ?? "", value: a?.value ?? "" })),
        })),
      }));

      groups.push({
        key,
        title,
        groupId: key,
        groupTitle: title,
        sspBaselineTotal: baselineTotal,
        summary: groupSummary,
        controls,
      });
      continue;
    }

    // Flat rows shape (legacy fallback)
    const ensure = (key: string, title: string) => {
      if (!byKey[key]) {
        byKey[key] = { key, title, controls: [] };
        groups.push(byKey[key]);
      }
      return byKey[key];
    };
    if (it?.groupHeader || it?.isGroupHeader) {
      ensure(String(it.groupId ?? it.key ?? ""), String(it.groupTitle ?? it.title ?? ""));
      continue;
    }
    const g = ensure(String(it?.groupId ?? it?.key ?? ""), String(it?.groupTitle ?? it?.title ?? ""));
    const control: ImplControl = {
      controlId: String(it?.controlId ?? ""),
      title: String(it?.controlTitle ?? it?.title ?? ""),
      status: (it?.implementationStatus ?? it?.status ?? "not-implemented") as ImplControl["status"],
      implementationStatus: String(it?.implementationStatus ?? it?.status ?? "not-implemented"),
      auditStatus: it?.auditStatus ? (String(it.auditStatus) as ImplControl["auditStatus"]) : undefined,
      isEnhancement: !!it?.isEnhancement,
      components: (it?.components ?? []).map((c: any) => ({ name: c?.name ?? "", type: c?.type ?? "" })),
      inventory: (it?.inventoryItems ?? it?.inventory ?? []).map((iv: any) => ({
        device: String(iv?.description ?? iv?.device ?? "").replace(/^Device:\s*/i, ""),
        component: iv?.componentName ?? iv?.component,
        assets: (iv?.assets ?? []).map((a: any) => ({ type: a?.type ?? "", value: a?.value ?? "" })),
      })),
    };
    g.controls.push(control);
  }
  return { profileTitle: "", groups };
}

/* report.incidentNotes (+ recentActivity) → Findings + Findings Snapshot */
function mapFindings(
  notes: any[] | undefined,
  activity: any[] | undefined,
): { findings: FindingsSection; snapshot: FindingsSnapshot } {
  const list = Array.isArray(notes) ? notes : [];
  const rows: FindingRow[] = list.map((n) => ({
    controlId: n?.findingId ?? "",
    title: n?.findingTitle ?? n?.controlTitle ?? "",
    status: n?.status ?? "identified_finding",
    severity: severityOf(n?.severity),
    controlTitle: n?.controlTitle,
    statementDescription: n?.techControlStatement,
    description: n?.description,
    createdAt: n?.createdAt,
    updatedAt: n?.updatedAt,
    poamItemUuid: n?.poamItemUuid,
    auditLogJson: n?.auditLogJson,
  }));
  const bySeverity = { critical: 0, high: 0, medium: 0, low: 0, none: 0 };
  const byStatus = { identified: 0, inProgress: 0, onHold: 0, validation: 0, completed: 0, closed: 0, riskAccepted: 0 };
  list.forEach((n) => {
    bySeverity[severityOf(n?.severity)]++;
    byStatus[STATUS_BUCKET[n?.status] ?? "identified"]++;
  });
  const act = Array.isArray(activity) ? activity : [];
  const recentActivity = act.map((a) => ({
    date: String(a?.timestamp ?? "").slice(0, 10),
    controlId: a?.controlId ?? "",
    title: a?.findingTitle ?? "",
    status: String(a?.status ?? "").replace(/_/g, " "),
  }));
  return {
    findings: { rows, recentActivity },
    snapshot: {
      total: list.length,
      bySeverity,
      byStatus,
      severityCoverageHint: bySeverity.none > list.length / 2,
      activity: act, // {timestamp,controlId,findingTitle,status,severity} — FSNAP_ACTIVITY shape
    },
  };
}

/* assessmentHistory → Compliance Trend (control-level) + Assessment History rows */
function mapTrend(ah: any[] | undefined): TrendSection {
  if (!Array.isArray(ah)) return { points: [] };
  return {
    points: ah.map((h) => {
      const passed = h?.passedControls ?? h?.passed ?? 0;
      const failed = h?.failedControls ?? h?.failed ?? 0;
      const totalStatements = h?.totalControls ?? (passed + failed);
      const totalApplicable = h?.controlCount ?? totalStatements;
      const scopePercent = totalApplicable > 0
        ? Math.round((totalStatements / totalApplicable) * 100)
        : 0;
      const compliancePercent = h?.controlCompliancePercent ?? h?.compliancePercent
        ?? (totalStatements > 0 ? Math.round((passed / totalStatements) * 100) : 0);
      return {
        title: h?.title ?? "",
        date: h?.endDate ?? "",
        createdAt: h?.createdAt ?? h?.startDate ?? h?.endDate ?? "",
        passed,
        failed,
        totalStatements,
        totalApplicable,
        scopePercent,
        compliancePercent,
        primary: !!h?.primary,
      };
    }),
  };
}
function mapHistory(ah: any[] | undefined): HistorySection {
  if (!Array.isArray(ah)) return { rows: [] };
  return {
    rows: ah.map((h) => ({
      plan: h?.title ?? "",
      status: h?.status ?? "",
      startDate: h?.startDate ?? "",
      endDate: h?.endDate ?? "",
      inPlan: h?.controlCount ?? h?.totalControls ?? 0,
      assessed: h?.totalControls ?? 0,
      passed: h?.passedControls ?? 0,
      failed: h?.failedControls ?? 0,
      compliancePercent: h?.controlCompliancePercent ?? 0,
      primary: !!h?.primary,
      pendingReview: h?.pendingReviewCount ?? 0,
    })),
  };
}

export interface MapReportOptions {
  scopedPlanId?: string;
  scopedPlanTitle?: string;
  generatedBy?: string;
}

export function mapReportData(
  raw: ReportDataDto | null | undefined,
  sspId: string,
  options?: MapReportOptions | string,
): ReportPayload {
  const scopedId = typeof options === "string" ? options : options?.scopedPlanId;
  const scopedTitle = typeof options === "object" ? options?.scopedPlanTitle : undefined;
  const scopedGeneratedBy = typeof options === "object" ? options?.generatedBy : undefined;

  const r = raw?.report ?? {};
  const history = Array.isArray(raw?.assessmentHistory) ? raw.assessmentHistory : [];

  // When scoped to a single assessment plan (Auditor report), locate that specific plan
  let selectedPlan: any = undefined;
  if (scopedId || scopedTitle) {
    selectedPlan = history.find((h: any) => {
      const idMatch = scopedId
        ? [h?.planId, h?.id, h?.assessmentPlanId, h?.plan_id]
            .filter(Boolean)
            .some((val) => String(val).toLowerCase() === scopedId.toLowerCase())
        : false;
      const titleMatch = scopedTitle
        ? String(h?.title ?? "").toLowerCase() === scopedTitle.toLowerCase()
        : false;
      return idMatch || titleMatch;
    });
  }

  const primaryPlan = selectedPlan ?? history.find((h: any) => h?.primary) ?? history[0];

  let assessmentPlans: string[];
  if (selectedPlan?.title) {
    assessmentPlans = [selectedPlan.title];
  } else if (scopedTitle) {
    assessmentPlans = [scopedTitle];
  } else if (scopedId) {
    const fallbackTitle = r.assessmentPlanName || r.planName || (history.length === 1 ? history[0]?.title : undefined);
    assessmentPlans = fallbackTitle ? [fallbackTitle] : history.map((h: any) => String(h?.title ?? "")).filter(Boolean);
  } else {
    assessmentPlans = history.map((h: any) => String(h?.title ?? "")).filter(Boolean);
  }

  const rawGeneratedBy =
    scopedGeneratedBy ??
    r.generatedBy ??
    r.createdBy ??
    r.createdByName ??
    r.author ??
    raw?.generatedBy ??
    raw?.createdBy ??
    raw?.createdByName ??
    raw?.author;

  let currentUserName = "";
  try {
    const user = useAuthStore.getState().user;
    currentUserName = user?.displayName || user?.username || "";
  } catch {
    // fallback in environments where useAuthStore may not be available
  }

  const generatedBy =
    (rawGeneratedBy && String(rawGeneratedBy).trim()) ||
    currentUserName ||
    "System";

  const rawCreatedDate =
    r.createdDate ??
    r.createdAt ??
    r.generatedOn ??
    raw?.createdDate ??
    raw?.createdAt ??
    raw?.generatedOn;

  const generatedOn = fmtDate(rawCreatedDate) || fmtDate(new Date().toISOString());

  const meta = {
    sspId,
    sspName: r.systemName ?? "",
    reportYear: new Date(rawCreatedDate ?? primaryPlan?.endDate ?? "").getFullYear() || new Date().getFullYear(),
    assessmentPlans,
    assessmentStartDate: primaryPlan?.startDate ?? "",
    assessmentEndDate: primaryPlan?.endDate ?? "",
    generatedOn,
    generatedBy,
  };

  const results = mapControlEvidences({
    controlEvidences: r.controlEvidences,
    controlStatusList: r.controlStatusList,
  });

  const cs = r.complianceStats; // {totalControls, passedControls, failedControls, pctControls, baselineTotal}
  const assessedCount = Math.max(cs?.totalControls ?? cs?.total ?? 0, results.length);
  const satisfiedFromResults = results.filter((row) => row.determination === "satisfied").length;
  const notSatisfiedFromResults = results.filter((row) => row.determination === "not-satisfied").length;
  const satisfiedCount = Math.max(cs?.passedControls ?? cs?.passed ?? 0, satisfiedFromResults);
  const notSatisfiedCount = Math.max(cs?.failedControls ?? cs?.failed ?? 0, notSatisfiedFromResults);
  const baselineTotal = Math.max(cs?.baselineTotal ?? cs?.totalControls ?? 0, assessedCount);

  const kpis = {
    assessed: assessedCount,
    baselineTotal: baselineTotal,
    satisfied: satisfiedCount,
    notSatisfied: notSatisfiedCount,
    compliancePercent:
      cs?.pctControls ??
      cs?.percent ??
      cs?.compliancePercent ??
      (assessedCount ? Math.round((satisfiedCount / assessedCount) * 100) : 0),
  };

  const si = r.systemImpact;
  const cia = {
    confidentiality: ciaLevel(si?.confidentiality),
    integrity: ciaLevel(si?.integrity),
    availability: ciaLevel(si?.availability),
    overall: ciaLevel(si?.overall ?? si?.overallImpact ?? r.sspLayer),
  };

  const executive: ExecutiveSection = {
    kpis,
    cia,
    narrative: [], // AI summary arrives via the separate executive-summary endpoint
    openPoamCount: Array.isArray(r.incidentNotes) ? r.incidentNotes.length : 0,
  };

  const { findings, snapshot } = mapFindings(r.incidentNotes, r.recentActivity);

  return {
    meta,
    visibleSections: [
      "sec:catalog", "sec:exec", "sec:control-impl", "sec:car",
      "sec:findings-snapshot", "sec:findings", "sec:trend", "sec:history",
    ],
    catalog: mapCatalog(r.catalogs, raw),
    executive,
    controlImplementation: mapControlImplementation(raw?.controlImplementations),
    assessment: {
      totalAssets: 0,
      passedAssets: 0,
      failedAssets: 0,
      results,
    },
    findingsSnapshot: snapshot,
    findings,
    trend: mapTrend(raw?.assessmentHistory),
    history: mapHistory(raw?.assessmentHistory),
  };
}
